/**
 * PoliceDepartmentsTable - Virtualized table with infinite scroll
 *
 * Clean, minimal design following the MattersTable pattern
 */

import {
  ArrowPathIcon,
  BuildingOffice2Icon,
} from '@heroicons/react/24/outline';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { ReadPoliceDepartmentWithStats } from '@/services/api/generated/policeDepartmentsService';

import { PoliceDepartmentFilters } from './PoliceDepartmentFilters';
import { PoliceDepartmentTableHeader } from './PoliceDepartmentTableHeader';
import { usePoliceDepartmentsTableColumns } from './usePoliceDepartmentsTableColumns';

interface PoliceDepartmentsTableProps {
  departments: ReadPoliceDepartmentWithStats[];
  totalCount: number;
  onLoadMore: () => void;
  isLoadingNewFilter: boolean;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  stateFilter: string;
  onStateFilterChange: (value: string) => void;
  onCreateClick: () => void;
  ordering: string;
  onOrderingChange: (value: string) => void;
}

export const PoliceDepartmentsTable = ({
  departments,
  totalCount,
  onLoadMore,
  isLoadingNewFilter,
  searchQuery,
  onSearchChange,
  stateFilter,
  onStateFilterChange,
  onCreateClick,
  ordering,
  onOrderingChange,
}: PoliceDepartmentsTableProps) => {
  const navigate = useNavigate();
  const tableContainerRef = React.useRef<HTMLDivElement>(null);

  const columns = usePoliceDepartmentsTableColumns(ordering, onOrderingChange);

  const table = useReactTable({
    data: departments,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const { rows } = table.getRowModel();

  const rowVirtualizer = useVirtualizer({
    count: totalCount,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => 56,
    overscan: 20,
  });

  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();

  const paddingTop = virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0;
  const paddingBottom =
    virtualRows.length > 0
      ? totalSize - (virtualRows?.[virtualRows.length - 1]?.end || 0)
      : 0;

  // Infinite scroll handler
  React.useEffect(() => {
    const lastVirtualRow = virtualRows[virtualRows.length - 1];
    if (!lastVirtualRow) return;

    const bufferSize = 20;
    const nearEndOfLoadedData =
      lastVirtualRow.index >= departments.length - bufferSize;

    if (nearEndOfLoadedData && departments.length < totalCount) {
      onLoadMore();
    }
  }, [virtualRows, departments.length, totalCount, onLoadMore]);

  const handleRowClick = React.useCallback(
    (departmentId: string) => {
      navigate(`/policedepartments/${departmentId}`);
    },
    [navigate],
  );

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col h-full min-h-0">
      {/* Header */}
      <PoliceDepartmentTableHeader
        totalCount={totalCount}
        onCreateClick={onCreateClick}
      />

      {/* Filters */}
      <PoliceDepartmentFilters
        searchQuery={searchQuery}
        onSearchChange={onSearchChange}
        stateFilter={stateFilter}
        onStateFilterChange={onStateFilterChange}
      />

      {/* Table Container */}
      {isLoadingNewFilter && (
        <div className="flex items-center justify-center flex-1 min-h-0">
          <ArrowPathIcon className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      )}

      {!isLoadingNewFilter && departments.length === 0 && (
        <div className="flex flex-col items-center justify-center flex-1 min-h-0 py-16">
          <div className="rounded-full bg-gray-100 p-3">
            <BuildingOffice2Icon className="h-10 w-10 text-gray-400" />
          </div>
          <h3 className="mt-4 text-base font-medium text-gray-900">
            No departments found
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Try adjusting your search or filters
          </p>
        </div>
      )}

      {!isLoadingNewFilter && departments.length > 0 && (
        <div
          ref={tableContainerRef}
          className="flex-1 min-h-0 overflow-auto"
          style={{ contain: 'strict' }}
        >
          <table className="w-full">
            <thead className="bg-gray-50 sticky top-0 z-10 border-b border-gray-200">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      style={{ width: header.getSize() }}
                      className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {paddingTop > 0 && (
                <tr>
                  <td
                    colSpan={columns.length}
                    style={{ height: `${paddingTop}px` }}
                  />
                </tr>
              )}
              {virtualRows.map((virtualRow) => {
                const row = rows[virtualRow.index];

                // Show loading placeholder for rows not yet loaded
                if (!row) {
                  return (
                    <tr
                      key={`placeholder-${virtualRow.index}`}
                      className="h-[56px]"
                    >
                      {columns.map((_, colIndex) => (
                        <td key={colIndex} className="px-4 py-3">
                          <div className="h-4 w-full animate-pulse rounded bg-gray-200" />
                        </td>
                      ))}
                    </tr>
                  );
                }

                return (
                  <tr
                    key={row.id}
                    onClick={() => handleRowClick(row.original.id)}
                    className="cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="px-4 py-3">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </td>
                    ))}
                  </tr>
                );
              })}
              {paddingBottom > 0 && (
                <tr>
                  <td
                    colSpan={columns.length}
                    style={{ height: `${paddingBottom}px` }}
                  />
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
